import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Hosts we're willing to bounce back to after logout. Without this an
// attacker could send someone to /logout?redirect=https://evil.example and
// use our domain to launder the redirect.
const ALLOWED_REDIRECT_HOSTS = [
  'clayworks.space',
  'www.clayworks.space',
  'app.clayworks.space',
  'book.clayworks.space',
  // Transition-period entries — remove once clayworks.co.in is fully
  // decommissioned (Stage 5 cutover, see .do/app.yaml).
  'clayworks.co.in',
  'www.clayworks.co.in',
];

function safeRedirectTarget(raw) {
  if (!raw) return null;
  try {
    const url = new URL(raw, window.location.origin);
    if (url.protocol !== 'https:' && url.hostname !== 'localhost') return null;
    return ALLOWED_REDIRECT_HOSTS.includes(url.hostname) || url.hostname === 'localhost'
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

/**
 * Single-logout endpoint, reachable cross-app.
 *
 * The website can clear the shared cookie by calling the API, but it can't
 * touch this origin's localStorage — where the bearer token that actually
 * keeps this app logged in lives. So it sends the browser here instead, and
 * this page does the part only this origin can do, then bounces back.
 */
export default function LogoutPage() {
  const [searchParams] = useSearchParams();
  const { logout } = useAuth();
  const hasRun = useRef(false);

  useEffect(() => {
    // StrictMode double-invokes effects in dev; logging out twice is
    // harmless but the redirect racing itself is not.
    if (hasRun.current) return;
    hasRun.current = true;

    const target = safeRedirectTarget(searchParams.get('redirect'));
    // Wait for the revoke request to leave the browser before navigating,
    // otherwise the unload cancels it and the server-side session survives.
    Promise.resolve(logout()).finally(() => {
      window.location.replace(target || '/login');
    });
  }, [logout, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-muted-foreground text-sm">Signing you out…</p>
    </div>
  );
}
